/**
 * @file Recalibration hooks — read the capture signal, draft
 *       proposals, and emit a content-PR artifact.
 * @module @labcraft/admin/ui/hooks/use-recalibration
 *
 * Three hooks live here:
 *
 *   - `useRecalibrationSignal()` — subscribe to the anonymized
 *     mentor-capture aggregate (`useQuery`, single global key).
 *   - `useRecalibrationDraftMutation()` — POST to ask the model for a
 *     draft (`useMutation`; the daemon reads the signal itself, so the
 *     mutation takes no variables).
 *   - `useRecalibrationContentPrMutation()` — POST the approved draft
 *     and receive the deterministic content-PR instruction artifact.
 *
 * Both mutations are stateless w.r.t. the cache — their results are
 * held in the panel's local React state, not written back to any query
 * key — so neither invalidates on success.
 *
 * Convention: hooks live in this directory and never call `useQuery` /
 * `useMutation` outside it.
 */

import { useMutation, useQuery } from '@tanstack/react-query'
import { useClient } from '@labcraft/core/frontend/react'
import type { Admin } from '../../client/index.ts'
import type {
  ContentPrArtifact,
  RecalibrationDraft,
  RecalibrationSignalResponse,
} from '../../shared/recalibration_routes.ts'
import { qkRecalibrationSignal } from './query_keys.ts'

/** Subscribe to the anonymized mentor-capture recalibration signal. */
export function useRecalibrationSignal() {
  const client = useClient<Admin>()
  return useQuery<RecalibrationSignalResponse>({
    queryKey: qkRecalibrationSignal,
    queryFn: () => client.recalibration.signal(),
  })
}

/**
 * Ask the daemon to draft recalibration proposals from the current
 * signal. The panel holds the returned draft in local state so the
 * operator can approve/reject per proposal before actuating.
 */
export function useRecalibrationDraftMutation() {
  const client = useClient<Admin>()
  return useMutation<RecalibrationDraft, Error, void>({
    mutationFn: () => client.recalibration.draft(),
  })
}

/**
 * Turn the approved subset of proposals into a content-PR instruction
 * artifact. Takes the approved `RecalibrationDraft` as its variable.
 */
export function useRecalibrationContentPrMutation() {
  const client = useClient<Admin>()
  return useMutation<ContentPrArtifact, Error, RecalibrationDraft>({
    mutationFn: draft => client.recalibration.contentPr(draft),
  })
}
